import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import {
  Edit,
  Delete,
  CheckCircle,
  Warning,
  Build as ToolIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

const StageNode = memo(({ data }) => {
  const { stage, onEdit, onDelete } = data;

  const hasPersona = !!stage.personaId;
  const toolCount = stage.availableTools?.length || 0;
  const assessmentCount = stage.requiredAssessments?.length || 0;
  const hasTransition = !!stage.transitionTrigger?.type;
  const hasGreeting = !!stage.greetingMessage;

  const isValid = hasPersona && hasTransition;

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />

      <Box
        sx={{
          padding: 2,
          border: `2px solid ${isValid ? '#4caf50' : '#ff9800'}`,
          borderRadius: 2,
          backgroundColor: 'white',
          minWidth: 260,
          maxWidth: 280,
          boxShadow: 3,
          '&:hover': {
            boxShadow: 6,
            borderColor: isValid ? '#2e7d32' : '#f57c00'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Chip
            label={`Stage ${stage.stageNumber}`}
            size="small"
            color="primary"
            sx={{ fontWeight: 'bold' }}
          />
          <Box>
            <IconButton size="small" onClick={onEdit} color="primary">
              <Edit fontSize="small" />
            </IconButton>
            {stage.stageNumber > 1 && (
              <IconButton size="small" onClick={onDelete} color="error">
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600 }}>
          {stage.stageName || 'Untitled Stage'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {hasPersona ? (
            <Chip
              size="small"
              icon={<CheckCircle />}
              label="Persona"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          ) : (
            <Chip
              size="small"
              icon={<Warning />}
              label="No Persona"
              color="warning"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}

          {toolCount > 0 && (
            <Chip
              size="small"
              icon={<ToolIcon />}
              label={`${toolCount} ${toolCount === 1 ? 'Tool' : 'Tools'}`}
              sx={{ fontSize: '0.7rem' }}
            />
          )}

          {assessmentCount > 0 && (
            <Chip
              size="small"
              icon={<AssessmentIcon />}
              label={`${assessmentCount} ${assessmentCount === 1 ? 'Assessment' : 'Assessments'}`}
              color="info"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {hasGreeting && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
            Has greeting message
          </Typography>
        )}

        {stage.transitionTrigger?.type && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontWeight: 500,
              mt: 1,
              pt: 1,
              borderTop: '1px solid #eee'
            }}
          >
            Transition: {stage.transitionTrigger.type.replace(/_/g, ' ')}
          </Typography>
        )}
      </Box>

      <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
    </>
  );
});

StageNode.displayName = 'StageNode';

export default StageNode;
